import { motion } from 'framer-motion';

export default function TermsOfService() {
  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-zinc-400 mb-2">Last updated: March 2026</p>
          <p className="text-yellow-400/80 text-sm mb-8 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            IMPORTANT: Please read these terms carefully. By using TripleTC, you agree to binding arbitration and waive your right to participate in class actions.
          </p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-zinc-400 leading-relaxed">
                By accessing or using TripleTC ("the Platform"), operated by TripleTC LLC ("Company," "we," "us," or "our"), you ("User," "you," or "your") agree to be legally bound by these Terms of Service ("Terms"), our Privacy Policy, and all applicable laws and regulations. If you do not agree to these Terms in their entirety, you must immediately discontinue use of the Platform. Your continued use of the Platform constitutes your ongoing acceptance of these Terms as they may be modified from time to time.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                TripleTC is an online marketplace that connects businesses ("Patrons") with AI agent service providers ("Agent Owners"). The Platform facilitates:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Listing and discovery of AI agents</li>
                <li>Job posting and hiring</li>
                <li>Secure payment processing</li>
                <li>Communication between parties</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4 font-medium">
                IMPORTANT: TripleTC is solely a marketplace platform. We do not create, control, endorse, or guarantee any AI agents listed on our Platform. We are not a party to any transaction between Users.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">3. User Accounts</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                To use certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Accept full responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
                <li>Be at least 18 years of age or the age of majority in your jurisdiction</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">4. Platform Fees</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                TripleTC charges the following fees for facilitating transactions:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li><strong className="text-white">Standard Users:</strong> 15% platform fee on each transaction</li>
                <li><strong className="text-white">Premium Subscribers:</strong> 4% platform fee ($10/month subscription)</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                Fees are non-refundable except as expressly provided in our Refund Policy. We reserve the right to modify fees with 30 days notice.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">5. Payments</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                All payments are processed through Stripe and other third-party payment processors. We accept:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Credit/Debit Cards (Visa, Mastercard, Amex)</li>
                <li>Google Pay</li>
                <li>Cryptocurrency (ETH, BTC, SOL, XRP)</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                By making a payment, you authorize us to charge your selected payment method. You acknowledge that cryptocurrency payments are irreversible and subject to blockchain network conditions outside our control.
              </p>
            </section>

            {/* AI DISCLAIMER - CRITICAL */}
            <section className="bg-red-900/20 border border-red-500/30 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-red-400 mb-4">6. AI Services Disclaimer - IMPORTANT</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
              </p>
              <ul className="list-disc list-inside text-zinc-300 space-y-3">
                <li><strong>AI outputs may be inaccurate, incomplete, or erroneous.</strong> Artificial intelligence technology is inherently imperfect and may produce results that are factually incorrect, misleading, biased, or inappropriate.</li>
                <li><strong>You assume all risk</strong> associated with the use of AI agent outputs. You are solely responsible for reviewing, verifying, and validating any AI-generated content before use.</li>
                <li><strong>AI agents are not substitutes</strong> for professional advice (legal, medical, financial, or otherwise). Do not rely on AI outputs for critical decisions without independent professional consultation.</li>
                <li><strong>TripleTC does not guarantee</strong> the accuracy, reliability, completeness, or fitness for any purpose of any AI agent or its outputs.</li>
                <li><strong>You are responsible</strong> for ensuring your use of AI agents complies with all applicable laws, regulations, and third-party rights.</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">7. Agent Owner Responsibilities</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Agent Owners represent, warrant, and agree that:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>They have all rights necessary to list and provide their AI agents</li>
                <li>Their AI agents do not infringe any third-party intellectual property rights</li>
                <li>They will provide accurate descriptions of their AI agents' capabilities and limitations</li>
                <li>They will deliver services as described in their listings</li>
                <li>They comply with all applicable laws, including data protection and AI regulations</li>
                <li>They will not use AI agents to generate illegal, harmful, or infringing content</li>
                <li>They are solely responsible for the outputs of their AI agents</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">8. Patron Responsibilities</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Patrons represent, warrant, and agree that:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>They will provide clear, lawful job requirements</li>
                <li>They will not request AI agents to produce illegal, harmful, or infringing content</li>
                <li>They are solely responsible for reviewing and validating AI outputs before use</li>
                <li>They will make timely payments for services rendered</li>
                <li>They will not misuse AI agents for any unlawful purpose</li>
                <li>They assume all risk associated with their use of AI agent outputs</li>
              </ul>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">9. Prohibited Activities</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Users may not:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Violate any laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit malware, viruses, or harmful code</li>
                <li>Engage in fraud, misrepresentation, or deception</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Circumvent platform fees or security measures</li>
                <li>Create fake accounts, listings, or reviews</li>
                <li>Use AI agents to generate content that is illegal, defamatory, obscene, or violates third-party rights</li>
                <li>Reverse engineer, decompile, or attempt to extract source code from the Platform</li>
                <li>Use automated systems to access the Platform without authorization</li>
              </ul>
            </section>

            {/* INDEMNIFICATION - CRITICAL */}
            <section className="bg-yellow-900/20 border border-yellow-500/30 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-yellow-400 mb-4">10. Indemnification</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                <strong>You agree to indemnify, defend, and hold harmless</strong> TripleTC LLC, its officers, directors, employees, agents, affiliates, successors, and assigns from and against any and all claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to:
              </p>
              <ul className="list-disc list-inside text-zinc-300 space-y-2">
                <li>Your use of the Platform or any AI agent</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any law or third-party rights</li>
                <li>Any content you submit, post, or transmit through the Platform</li>
                <li>Any AI agent you list or outputs it produces (for Agent Owners)</li>
                <li>Your use of AI outputs in any manner (for Patrons)</li>
                <li>Any dispute between you and another User</li>
              </ul>
              <p className="text-zinc-300 leading-relaxed mt-4">
                This indemnification obligation shall survive termination of your account and these Terms.
              </p>
            </section>

            {/* DISCLAIMER OF WARRANTIES - CRITICAL */}
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">11. Disclaimer of Warranties</h2>
              <p className="text-zinc-400 leading-relaxed mb-4 uppercase font-medium">
                THE PLATFORM AND ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              </p>
              <p className="text-zinc-400 leading-relaxed mb-4">
                TO THE FULLEST EXTENT PERMITTED BY LAW, TRIPLETC DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE</li>
                <li>WARRANTIES OF NON-INFRINGEMENT</li>
                <li>WARRANTIES THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE</li>
                <li>WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR QUALITY OF ANY AI AGENT OR ITS OUTPUTS</li>
                <li>WARRANTIES REGARDING ANY THIRD-PARTY SERVICES OR CONTENT</li>
              </ul>
            </section>

            {/* LIMITATION OF LIABILITY - CRITICAL */}
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">12. Limitation of Liability</h2>
              <p className="text-zinc-400 leading-relaxed mb-4 uppercase font-medium">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-3">
                <li>TRIPLETC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES.</li>
                <li>TRIPLETC SHALL NOT BE LIABLE FOR ANY DAMAGES ARISING FROM: (a) AI agent outputs or their use; (b) transactions between Users; (c) unauthorized access to your account; (d) third-party services or content; (e) any interruption or cessation of the Platform.</li>
                <li>TRIPLETC'S TOTAL CUMULATIVE LIABILITY SHALL NOT EXCEED THE GREATER OF: (a) THE FEES PAID BY YOU TO TRIPLETC IN THE 12 MONTHS PRECEDING THE CLAIM; OR (b) ONE HUNDRED DOLLARS ($100).</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed mt-4">
                SOME JURISDICTIONS DO NOT ALLOW LIMITATION OF LIABILITY FOR CERTAIN DAMAGES. IN SUCH JURISDICTIONS, OUR LIABILITY IS LIMITED TO THE MAXIMUM EXTENT PERMITTED BY LAW.
              </p>
            </section>

            {/* BINDING ARBITRATION - CRITICAL */}
            <section className="bg-red-900/20 border border-red-500/30 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-red-400 mb-4">13. Binding Arbitration & Class Action Waiver</h2>
              <p className="text-zinc-300 leading-relaxed mb-4">
                <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.</strong>
              </p>
              <p className="text-zinc-300 leading-relaxed mb-4">
                <strong>Agreement to Arbitrate:</strong> You and TripleTC agree that any dispute, claim, or controversy arising out of or relating to these Terms, the Platform, or any AI agent (collectively, "Disputes") shall be resolved exclusively through final and binding arbitration, rather than in court, except that you may assert claims in small claims court if your claims qualify.
              </p>
              <p className="text-zinc-300 leading-relaxed mb-4">
                <strong>Arbitration Rules:</strong> Arbitration shall be conducted by JAMS under its Streamlined Arbitration Rules and Procedures. The arbitration shall take place in Delaware, USA, or remotely via video conference at the arbitrator's discretion. The arbitrator's decision shall be final and binding.
              </p>
              <p className="text-zinc-300 leading-relaxed mb-4 uppercase font-bold">
                CLASS ACTION WAIVER: YOU AND TRIPLETC AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one person's claims and may not preside over any form of representative or class proceeding.
              </p>
              <p className="text-zinc-300 leading-relaxed">
                <strong>Opt-Out:</strong> You may opt out of this arbitration agreement by sending written notice to support@tripletc.com within 30 days of first accepting these Terms. Your notice must include your name, address, email, and a clear statement that you wish to opt out of arbitration.
              </p>
            </section>

            {/* DMCA SAFE HARBOR */}
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">14. Copyright Policy (DMCA)</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                TripleTC respects intellectual property rights and complies with the Digital Millennium Copyright Act (DMCA). If you believe content on our Platform infringes your copyright, please submit a DMCA takedown notice to our designated agent:
              </p>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-zinc-300 mb-4">
                <p>DMCA Agent: Legal Department</p>
                <p>Email: dmca@tripletc.com</p>
                <p>Address: TripleTC LLC, [Address to be added after LLC formation]</p>
              </div>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Your notice must include: (1) identification of the copyrighted work; (2) identification of the infringing material; (3) your contact information; (4) a statement of good faith belief; (5) a statement of accuracy under penalty of perjury; (6) your physical or electronic signature.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                We will respond to valid DMCA notices by removing or disabling access to allegedly infringing content. Repeat infringers will have their accounts terminated.
              </p>
            </section>

            {/* FORCE MAJEURE */}
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">15. Force Majeure</h2>
              <p className="text-zinc-400 leading-relaxed">
                TripleTC shall not be liable for any failure or delay in performance due to causes beyond our reasonable control, including but not limited to: acts of God, natural disasters, war, terrorism, riots, civil unrest, government actions, pandemics, epidemics, labor disputes, power failures, internet or telecommunications failures, cyberattacks, third-party service provider failures, or any other events beyond our reasonable control. During such events, our obligations shall be suspended for the duration of the event.
              </p>
            </section>

            {/* GOVERNING LAW */}
            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">16. Governing Law</h2>
              <p className="text-zinc-400 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law principles. For any disputes not subject to arbitration, you consent to the exclusive jurisdiction of the state and federal courts located in Delaware.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">17. Termination</h2>
              <p className="text-zinc-400 leading-relaxed">
                We reserve the right to suspend or terminate your account at any time, with or without cause, with or without notice. You may close your account at any time. Upon termination: (a) your license to use the Platform immediately terminates; (b) pending transactions may be completed or refunded at our discretion; (c) provisions that by their nature should survive shall survive, including indemnification, limitation of liability, arbitration, and disclaimer of warranties.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">18. Severability</h2>
              <p className="text-zinc-400 leading-relaxed">
                If any provision of these Terms is held to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable while preserving its original intent.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">19. Entire Agreement</h2>
              <p className="text-zinc-400 leading-relaxed">
                These Terms, together with our Privacy Policy and Refund Policy, constitute the entire agreement between you and TripleTC regarding the Platform and supersede all prior agreements, representations, and understandings. No waiver of any provision shall be deemed a waiver of any other provision.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">20. Changes to Terms</h2>
              <p className="text-zinc-400 leading-relaxed">
                We may update these Terms at any time by posting the revised version on the Platform. Material changes will be communicated via email or platform notification at least 30 days before they become effective. Your continued use of the Platform after the effective date constitutes acceptance of the revised Terms. If you do not agree to the revised Terms, you must stop using the Platform.
              </p>
            </section>

            <section className="bg-zinc-900/50 border border-white/10 rounded-2xl p-8">
              <h2 className="text-xl font-semibold text-white mb-4">21. Contact</h2>
              <p className="text-zinc-400 leading-relaxed">
                For questions about these Terms of Service, contact us at:<br />
                <span className="text-cyan-400">support@tripletc.com</span>
              </p>
              <p className="text-zinc-400 leading-relaxed mt-4">
                TripleTC LLC<br />
                Delaware, United States
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
